/**
 * POC Data Storage Utility
 * Manages Point of Contact data in sessionStorage for validation and auto-fill
 * sessionStorage is used instead of localStorage to ensure data clears on tab/browser close
 */

const POC_STORAGE_KEY = 'ifta_poc_data';

/**
 * Save POC data to sessionStorage
 * @param {Object} pocData - POC information
 * @param {string} pocData.pocFirstName - POC first name
 * @param {string} pocData.pocLastName - POC last name
 * @param {string} pocData.pocEmail - POC email
 * @param {string} pocData.pocCountryCode - POC country code
 * @param {string} pocData.pocPhone - POC phone number
 */
export const savePOCData = (pocData) => {
  try {
    if (!pocData) {
      console.warn('Attempted to save null/undefined POC data');
      return;
    }

    // Validate required fields
    const requiredFields = ['pocFirstName', 'pocLastName', 'pocEmail', 'pocPhone'];
    const missingFields = requiredFields.filter(field => !pocData[field]);

    if (missingFields.length > 0) {
      console.warn(`POC data missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    sessionStorage.setItem(POC_STORAGE_KEY, JSON.stringify(pocData));
    console.log('POC data saved to sessionStorage');
  } catch (error) {
    console.error('Failed to save POC data to sessionStorage:', error);
  }
};

/**
 * Get POC data from sessionStorage
 * @returns {Object|null} POC data or null if not found
 */
export const getPOCData = () => {
  try {
    const data = sessionStorage.getItem(POC_STORAGE_KEY);
    if (!data) {
      return null;
    }

    const pocData = JSON.parse(data);
    return pocData;
  } catch (error) {
    console.error('Failed to retrieve POC data from sessionStorage:', error);
    return null;
  }
};

/**
 * Clear POC data from sessionStorage
 * Should be called on logout
 */
export const clearPOCData = () => {
  try {
    sessionStorage.removeItem(POC_STORAGE_KEY);
    console.log('POC data cleared from sessionStorage');
  } catch (error) {
    console.error('Failed to clear POC data from sessionStorage:', error);
  }
};

/**
 * Check if POC data exists in sessionStorage
 * @returns {boolean} True if POC data exists
 */
export const hasPOCData = () => {
  try {
    return sessionStorage.getItem(POC_STORAGE_KEY) !== null;
  } catch (error) {
    console.error('Failed to check POC data existence:', error);
    return false;
  }
};

/**
 * Validate if presenter data matches POC data
 * IMPORTANT: This validates for MIXED data (data quality issue)
 * - ALLOW if ALL fields match POC (POC is the presenter - OK!)
 * - ALLOW if NO fields match POC (different person - OK!)
 * - BLOCK if SOME fields match POC (mixed data - DATA QUALITY ISSUE!)
 *
 * Note: Only validates when ALL THREE fields (name, email, phone) are filled
 *
 * @param {Object} presenterData - Presenter information to validate
 * @param {Object} pocData - POC information to compare against
 * @returns {Object} Validation result with errors array
 */
export const validatePresenterVsPOC = (presenterData, pocData) => {
  if (!pocData) {
    return { isValid: true, errors: [] };
  }

  // Helper function to normalize strings for comparison
  const normalize = (str) => (str || '').trim().toLowerCase();

  // Helper function to normalize phone numbers (remove all non-digits)
  const normalizePhone = (phone) => (phone || '').replace(/\D/g, '');

  // Check if all required fields are filled before validating
  const presenterFullName = normalize(`${presenterData.presenterFirstName} ${presenterData.presenterLastName}`);
  const presenterEmail = normalize(presenterData.presenterEmail);
  const presenterPhone = normalizePhone(presenterData.presenterMobile);

  // IMPORTANT: Only validate if ALL THREE fields are filled
  // If user is still filling the form, don't show validation errors
  const hasName = presenterFullName.length > 0;
  const hasEmail = presenterEmail.length > 0;
  const hasPhone = presenterPhone.length > 0;

  if (!hasName || !hasEmail || !hasPhone) {
    // Not all fields filled yet, skip validation
    return { isValid: true, errors: [] };
  }

  // Now all fields are filled, proceed with validation
  const pocFullName = normalize(`${pocData.pocFirstName} ${pocData.pocLastName}`);
  const pocEmail = normalize(pocData.pocEmail);
  const pocPhone = normalizePhone(pocData.pocPhone);

  const matchedFields = [];
  const unmatchedFields = [];

  // Check name match
  if (presenterFullName === pocFullName) {
    matchedFields.push('name');
  } else {
    unmatchedFields.push('name');
  }

  // Check email match
  if (presenterEmail === pocEmail) {
    matchedFields.push('email');
  } else {
    unmatchedFields.push('email');
  }

  // Check phone match
  if (presenterPhone === pocPhone) {
    matchedFields.push('phone');
  } else {
    unmatchedFields.push('phone');
  }

  // Count how many fields match
  const matchCount = matchedFields.length;

  // VALIDATION LOGIC:
  // - 0 matches = All different (different person) → ALLOW ✅
  // - 3 matches = All same (POC is presenter) → ALLOW ✅
  // - 1-2 matches = Mixed data (DATA QUALITY ISSUE) → BLOCK ❌

  if (matchCount === 1 || matchCount === 2) {
    // BLOCK: Mixed POC and non-POC data (data quality issue)
    const errors = [{
      field: 'mixed',
      message: `Mixed data detected: ${matchedFields.map(f => f.toUpperCase()).join(', ')} match${matchedFields.length > 1 ? '' : 'es'} POC, but ${unmatchedFields.map(f => f.toUpperCase()).join(', ')} ${unmatchedFields.length > 1 ? 'do' : 'does'} not. You must either use all POC details OR all different details.`,
      matchedFields,
      unmatchedFields,
      matchCount
    }];

    return {
      isValid: false,
      errors: errors
    };
  }

  // ALLOW: Either all match (POC is presenter) or all different (someone else)
  return {
    isValid: true,
    errors: []
  };
};
